import type { WorkflowPerformanceRow } from '../data/reports';
import type { ApiResult } from './types';
import { reportsApiRequest } from './apiClient';
import { cachedReportFetch, dedupeReportSearch } from './reportLookupCache';
import {
  downloadReportBlob,
  exportReportBlob,
  type ReportExportFile,
  extractReportLookupRows,
  extractReportSearchPagePayload,
  mapReportSearchPage,
  nullIfEmpty,
  toCount,
  toLabel,
  displayReportCell,
  formatOfficerWithDivision,
  withReportDateRange,
  withReportTimeZone,
  type ReportSearchPage,
} from './reportApiCommon';

export type ReportExportFormat = 'PDF' | 'EXCEL';

export type WorkflowPerformanceReportFilters = {
  workflowTemplateIds: string[];
  fileCategoryIds: string[];
  subFileCategoryIds: string[];
  workflowStageIds: string[];
  allocationOfficerIds: string[];
  workflowTemplateStatusCodes: string[];
  dateFrom: string;
  dateTo: string;
};

export type WorkflowPerformanceLookupOption = {
  id: string;
  name: string;
};

export type WorkflowPerformanceTemplateStatusOption = {
  name: string;
  code: string;
};

/** Workflow Performance Report — template status filter options (code sent to API, name shown in UI). */
export const WORKFLOW_PERFORMANCE_TEMPLATE_STATUSES: WorkflowPerformanceTemplateStatusOption[] = [
  { code: 'ACTIVE', name: 'Active' },
  { code: 'SUBMITTED', name: 'Inactive' },
];

type ApiLookupRow = {
  id?: string | number;
  name?: string;
  display_name?: string;
  displayName?: string;
};

type ApiAllocationOfficerLookupRow = ApiLookupRow & {
  user_id?: string | number;
  allocation_officer_id?: string | number;
  allocation_officer_name?: string;
  division?: string | null;
};

function mapAllocationOfficerLookupRow(row: ApiAllocationOfficerLookupRow): WorkflowPerformanceLookupOption | null {
  const id = String(row.id ?? row.user_id ?? row.allocation_officer_id ?? '').trim();
  const name = formatOfficerWithDivision(
    row.display_name ?? row.displayName ?? row.name ?? row.allocation_officer_name,
    row.division,
  );
  if (!id || name === '—') return null;
  return { id, name };
}

function mapLookupRow(row: ApiLookupRow): WorkflowPerformanceLookupOption | null {
  const id = row.id != null ? String(row.id).trim() : '';
  const name = (
    row.display_name ??
    row.displayName ??
    row.name ??
    (row as { workflow_stage_name?: string }).workflow_stage_name ??
    (row as { stage_name?: string }).stage_name ??
    ''
  ).trim();
  if (!id || !name) return null;
  return { id, name };
}

type ApiSearchRow = {
  workflow_template_id?: string;
  id?: string;
  workflow_code?: string;
  workflowCode?: string;
  workflow_template_code?: string;
  workflow_template_name?: string;
  workflowTemplateName?: string;
  workflow_template_status?: string | null;
  workflowTemplateStatus?: string | null;
  template_status?: string | null;
  templateStatus?: string | null;
  workflow_template_status_name?: string | null;
  workflowTemplateStatusName?: string | null;
  file_category?: string | null;
  file_category_name?: string;
  fileCategory?: string;
  sub_file_category?: string | null;
  sub_file_category_name?: string;
  subFileCategory?: string;
  allocation_officer?: string | null;
  allocation_officer_name?: string;
  allocationOfficer?: string;
  division?: string | null;
  workflow_stages?: string | string[] | null;
  workflow_stage_names?: string | string[];
  workflowStages?: string | string[];
  workflow_stage_name?: string;
  workflowStage?: string;
  cases_received?: number | string | null;
  casesReceived?: number | string | null;
  completed?: number | string | null;
  completed_count?: number | string | null;
  in_progress?: number | string | null;
  inProgress?: number | string | null;
  overdue?: number | string | null;
  overdue_count?: number | string | null;
  escalated?: number | string | null;
  escalated_count?: number | string | null;
};

export function sortWorkflowPerformanceLookupOptions(
  options: WorkflowPerformanceLookupOption[],
): WorkflowPerformanceLookupOption[] {
  return [...options].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

export function dedupeWorkflowPerformanceLookupByName(
  options: WorkflowPerformanceLookupOption[],
): WorkflowPerformanceLookupOption[] {
  const seen = new Map<string, WorkflowPerformanceLookupOption>();
  for (const opt of options) {
    const key = opt.name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.set(key, opt);
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Unique display names from lookup rows (preserves first-seen casing per name). */
export function workflowPerformanceLookupDisplayNames(
  options: WorkflowPerformanceLookupOption[],
  fallback: readonly string[] = [],
): string[] {
  const deduped = dedupeWorkflowPerformanceLookupByName(options);
  if (deduped.length) return deduped.map((o) => o.name);
  return [...fallback];
}

export function mapWorkflowPerformanceLookupNamesToIds(
  options: WorkflowPerformanceLookupOption[],
  names: string[],
  matchAllIdsForName = false,
): string[] {
  const ids: string[] = [];
  for (const name of names) {
    const key = name.trim().toLowerCase();
    const matches = options.filter((o) => o.name.trim().toLowerCase() === key);
    if (matchAllIdsForName) {
      ids.push(...matches.map((o) => o.id));
    } else if (matches[0]) {
      ids.push(matches[0].id);
    }
  }
  return [...new Set(ids)];
}

export function mapWorkflowTemplateStatusNamesToCodes(
  options: WorkflowPerformanceTemplateStatusOption[],
  names: string[],
): string[] {
  const codes: string[] = [];
  for (const name of names) {
    const key = name.trim().toLowerCase();
    const match = options.find((o) => o.name.trim().toLowerCase() === key);
    if (match) codes.push(match.code);
  }
  return [...new Set(codes)];
}

async function listLookup(path: string): Promise<ApiResult<WorkflowPerformanceLookupOption[]>> {
  return cachedReportFetch(`workflow-performance:${path}`, async () => {
    const res = await reportsApiRequest<unknown>(path, { method: 'GET' });
    if (!res.ok) return { success: false, message: res.message };
    const mapped = extractReportLookupRows<ApiLookupRow>(res.data)
      .map(mapLookupRow)
      .filter((row): row is WorkflowPerformanceLookupOption => row !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { success: true, data: mapped };
  }, {
    cacheWhen: (result) => result.success,
  });
}

export const listWorkflowPerformanceTemplates = () => listLookup('/workflow-performance/workflow-templates');
export const listWorkflowPerformanceWorkflowStages = () => listLookup('/workflow-performance/workflow-stages');

async function listWorkflowStageLookupOptions(
  path: string,
  cacheKey: string,
  signal?: AbortSignal,
): Promise<ApiResult<WorkflowPerformanceLookupOption[]>> {
  return cachedReportFetch(
    cacheKey,
    async () => {
      const res = await reportsApiRequest<unknown>(path, { method: 'GET', signal });
      if (!res.ok) return { success: false, message: res.message };
      const mapped = extractReportLookupRows<ApiLookupRow>(res.data)
        .map(mapLookupRow)
        .filter((row): row is WorkflowPerformanceLookupOption => row !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      return { success: true, data: mapped };
    },
    { cacheWhen: (result) => result.success },
  );
}

function appendCommaSeparatedIds(qs: URLSearchParams, key: string, ids: string[]) {
  const filtered = ids.map((id) => id.trim()).filter(Boolean);
  if (filtered.length) qs.set(key, filtered.join(','));
}

/** Build GET /workflow-performance/workflow-stages/cascade query (`workflow_template_ids=id1,id2`). */
export function buildWorkflowPerformanceWorkflowStagesCascadeQuery(input: {
  workflowTemplateIds?: string[];
  categoryIds?: string[];
  subCategoryIds?: string[];
}): string {
  const qs = new URLSearchParams();
  appendCommaSeparatedIds(qs, 'workflow_template_ids', input.workflowTemplateIds ?? []);
  appendCommaSeparatedIds(qs, 'category_ids', input.categoryIds ?? []);
  appendCommaSeparatedIds(qs, 'sub_category_ids', input.subCategoryIds ?? []);
  const query = qs.toString();
  return query ? `?${query}` : '';
}

/** CMS — workflow stages filtered by workflow template, file category, and/or sub file category. */
export async function listWorkflowPerformanceWorkflowStagesCascade(
  input: {
    workflowTemplateIds?: string[];
    categoryIds?: string[];
    subCategoryIds?: string[];
    signal?: AbortSignal;
  },
): Promise<ApiResult<WorkflowPerformanceLookupOption[]>> {
  const path = `/workflow-performance/workflow-stages/cascade${buildWorkflowPerformanceWorkflowStagesCascadeQuery({
    workflowTemplateIds: input.workflowTemplateIds,
    categoryIds: input.categoryIds,
    subCategoryIds: input.subCategoryIds,
  })}`;
  const cacheKey = `workflow-performance:workflow-stages:cascade:${path.split('?')[1] ?? ''}`;
  return listWorkflowStageLookupOptions(path, cacheKey, input.signal);
}

async function listAllocationOfficerOptions(
  path: string,
  cacheKey: string,
  signal?: AbortSignal,
): Promise<ApiResult<WorkflowPerformanceLookupOption[]>> {
  return cachedReportFetch(
    cacheKey,
    async () => {
      const res = await reportsApiRequest<unknown>(path, { method: 'GET', signal });
      if (!res.ok) return { success: false, message: res.message };
      const mapped = extractReportLookupRows<ApiAllocationOfficerLookupRow>(res.data)
        .map(mapAllocationOfficerLookupRow)
        .filter((row): row is WorkflowPerformanceLookupOption => row !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      return { success: true, data: mapped };
    },
    { cacheWhen: (result) => result.success },
  );
}

/** CMS — allocation officers filtered by workflow template / file / sub file category. */
export async function listWorkflowPerformanceAllocationOfficersCascade(
  input: {
    workflowTemplateIds?: string[];
    categoryIds?: string[];
    subCategoryIds?: string[];
    signal?: AbortSignal;
  },
): Promise<ApiResult<WorkflowPerformanceLookupOption[]>> {
  const workflowTemplateIds = (input.workflowTemplateIds ?? []).map((id) => id.trim()).filter(Boolean);
  const categoryIds = (input.categoryIds ?? []).map((id) => id.trim()).filter(Boolean);
  const subCategoryIds = (input.subCategoryIds ?? []).map((id) => id.trim()).filter(Boolean);

  const qs = new URLSearchParams();
  appendCommaSeparatedIds(qs, 'workflow_template_ids', workflowTemplateIds);
  appendCommaSeparatedIds(qs, 'category_ids', categoryIds);
  appendCommaSeparatedIds(qs, 'sub_category_ids', subCategoryIds);

  const path = `/workflow-performance/allocation-officers/cascade?${qs.toString()}`;
  const cacheKey = `workflow-performance:allocation-officers:cascade:${qs.toString()}`;

  return listAllocationOfficerOptions(path, cacheKey, input.signal);
}

function buildWorkflowPerformanceFilterBody(filters: WorkflowPerformanceReportFilters): Record<string, unknown> {
  return {
    workflow_template_ids: nullIfEmpty(filters.workflowTemplateIds),
    category_ids: nullIfEmpty(filters.fileCategoryIds),
    sub_category_ids: nullIfEmpty(filters.subFileCategoryIds),
    workflow_stage_ids: nullIfEmpty(filters.workflowStageIds),
    allocation_officer_ids: nullIfEmpty(filters.allocationOfficerIds),
    template_status_codes: nullIfEmpty(filters.workflowTemplateStatusCodes),
  };
}

function buildSearchBody(filters: WorkflowPerformanceReportFilters): Record<string, unknown> {
  return withReportDateRange(
    buildWorkflowPerformanceFilterBody(filters),
    filters.dateFrom,
    filters.dateTo,
    { from: 'date_from', to: 'date_to' },
  );
}

function buildExportBody(filters: WorkflowPerformanceReportFilters): Record<string, unknown> {
  return withReportTimeZone(buildSearchBody(filters));
}

function mapSearchRow(row: ApiSearchRow, index: number): WorkflowPerformanceRow {
  const workflowStage =
    toLabel(row.workflow_stages ?? row.workflow_stage_names ?? row.workflowStages) ||
    (row.workflow_stage_name ?? row.workflowStage ?? '').trim();
  const workflowCode = displayReportCell(row.workflow_code ?? row.workflowCode ?? row.workflow_template_code ?? null);

  const templateStatusRaw =
    (row.workflow_template_status_name ??
      row.workflowTemplateStatusName ??
      row.workflow_template_status ??
      row.workflowTemplateStatus ??
      row.template_status ??
      row.templateStatus ??
      null) as string | null | undefined;

  const templateStatusCode = templateStatusRaw?.trim().toUpperCase();
  const templateStatusMatch = templateStatusCode
    ? WORKFLOW_PERFORMANCE_TEMPLATE_STATUSES.find((s) => s.code === templateStatusCode)
    : undefined;
  const workflowTemplateStatus = templateStatusMatch ? templateStatusMatch.name : displayReportCell(templateStatusRaw);
  return {
    id: (row.workflow_template_id ?? row.id ?? `wpr-${index}`).trim(),
    workflowCode,
    workflowTemplateName: displayReportCell(row.workflow_template_name ?? row.workflowTemplateName),
    workflowTemplateStatus,
    fileCategory: displayReportCell(row.file_category ?? row.file_category_name ?? row.fileCategory),
    subFileCategory: displayReportCell(row.sub_file_category ?? row.sub_file_category_name ?? row.subFileCategory),
    allocationOfficer: formatOfficerWithDivision(
      row.allocation_officer ?? row.allocation_officer_name ?? row.allocationOfficer,
      row.division,
    ),
    workflowStage: displayReportCell(workflowStage),
    casesReceived: toCount(row.cases_received ?? row.casesReceived),
    completed: toCount(row.completed ?? row.completed_count),
    inProgress: toCount(row.in_progress ?? row.inProgress),
    overdue: toCount(row.overdue ?? row.overdue_count),
    escalated: toCount(row.escalated ?? row.escalated_count),
  };
}

export async function searchWorkflowPerformanceReport(
  filters: WorkflowPerformanceReportFilters,
  page: number,
  size: number,
): Promise<ApiResult<ReportSearchPage<WorkflowPerformanceRow>>> {
  const body = buildSearchBody(filters);
  const searchKey = `workflow-performance:search:${page}:${size}:${JSON.stringify(body)}`;

  return dedupeReportSearch(searchKey, async () => {
    const res = await reportsApiRequest<unknown>(`/workflow-performance/search?page=${page}&size=${size}`, {
      method: 'POST',
      body,
    });
    if (!res.ok) return { success: false, message: res.message };

    const pagePayload = extractReportSearchPagePayload<ApiSearchRow>(res.data);
    const content = (pagePayload?.content ?? []).map(mapSearchRow);
    const mapped = mapReportSearchPage({ ...pagePayload, content }, page, size);
    return { success: true, data: mapped };
  });
}

export async function exportWorkflowPerformanceReport(
  format: ReportExportFormat,
  filters: WorkflowPerformanceReportFilters,
): Promise<ApiResult<ReportExportFile>> {
  return exportReportBlob(
    `/workflow-performance/export?format=${encodeURIComponent(format)}`,
    buildExportBody(filters),
  );
}

export { downloadReportBlob };
